import { BootstrapData } from '../api/types';
import { getWindow, isInWindow } from './horizons';
import { computeMonthBudget } from './month-budget';

/** Подготовка данных для экрана «Тренды»: sankey, бёрндаун, MoM, 6 месяцев. */

// монохром-земля под Антверпен: графит → мох → песок → охра → глина
const PALETTE = ['#2B2A24', '#4F5A43', '#8B9A82', '#A89A7C', '#9a9483', '#8E3B2A', '#6E6A5C', '#5F6B52'];
const NO_GROUP = 'Прочее';
export const RU_MON_SHORT = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

/** Стабильные цвета групп (по алфавиту имён). */
export function buildGroupColors(data: BootstrapData): Map<string, string> {
  const names = Array.from(
    new Set(
      data.categories.filter((c) => c.type === 'Расход').map((c) => c.grp || NO_GROUP),
    ),
  ).sort();
  const m = new Map<string, string>();
  names.forEach((n, i) => m.set(n, PALETTE[i % PALETTE.length]));
  return m;
}

// ---------------------------------------------------------------------------
// Sankey текущего месяца: доходы → пул → группы (+ отложено / из запасов)
// ---------------------------------------------------------------------------

export interface SankeyNode {
  name: string;
  value: number;
  color: string;
}

export interface SankeyData {
  left: SankeyNode[];
  right: SankeyNode[];
  total: number;
}

export function buildSankey(data: BootstrapData, now: Date = new Date()): SankeyData | null {
  const win = getWindow(1, { statsSince: data.settings.stats_since, now });
  const catBy = new Map(data.categories.map((c) => [c.name, c] as const));
  const colors = buildGroupColors(data);

  const incomeBy = new Map<string, number>();
  const groupBy = new Map<string, number>();
  for (const op of data.operations) {
    if (!isInWindow(op.date, win)) continue;
    const cat = catBy.get(op.category);
    const t = cat?.type ?? op.type;
    if (t === 'Доход') {
      incomeBy.set(op.category, (incomeBy.get(op.category) || 0) + op.amount);
    } else if (t === 'Расход') {
      const g = cat?.grp || NO_GROUP;
      groupBy.set(g, (groupBy.get(g) || 0) + op.amount);
    }
  }

  const incomeSum = Array.from(incomeBy.values()).reduce((s, v) => s + v, 0);
  const expenseSum = Array.from(groupBy.values()).reduce((s, v) => s + v, 0);
  if (incomeSum === 0 && expenseSum === 0) return null;

  const left: SankeyNode[] = Array.from(incomeBy.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([name, value]) => ({
      name,
      value,
      color: '#2B2A24',
    }));

  const right: SankeyNode[] = Array.from(groupBy.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([name, value]) => ({ name, value, color: colors.get(name) ?? '#8A8276' }));

  const saved = incomeSum - expenseSum;
  if (saved > 0) right.push({ name: 'Отложено', value: saved, color: '#4F5A43' });
  else if (saved < 0) left.push({ name: 'из запасов', value: -saved, color: '#8E3B2A' });

  return { left, right, total: Math.max(incomeSum, expenseSum) };
}

// ---------------------------------------------------------------------------
// Бёрндаун повседневного пула
// ---------------------------------------------------------------------------

export interface BurndownData {
  daysTotal: number;
  today: number;
  budget: number;
  /** Кумулятивные повседневные траты по дням 1..today. */
  cumulative: number[];
  projected: number;
}

export function buildBurndown(data: BootstrapData, now: Date = new Date()): BurndownData {
  const mb = computeMonthBudget(data.operations, data.categories, data.settings, now);
  const budget = mb.active && mb.availableFlexTotal > 0 ? mb.availableFlexTotal : mb.flexPlan;

  const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const catBy = new Map(data.categories.map((c) => [c.name, c] as const));
  const today = now.getDate();
  const daysTotal = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();

  const byDay = new Array<number>(daysTotal + 1).fill(0);
  for (const op of data.operations) {
    if (!op.date.startsWith(monthKey)) continue;
    const cat = catBy.get(op.category);
    const t = cat?.type ?? op.type;
    if (t !== 'Расход') continue;
    if (cat && !cat.is_daily) continue;
    const d = Number(op.date.slice(8, 10));
    if (d >= 1 && d <= daysTotal) byDay[d] += op.amount;
  }

  const cumulative: number[] = [];
  let acc = 0;
  for (let d = 1; d <= Math.min(today, daysTotal); d++) {
    acc += byDay[d];
    cumulative.push(acc);
  }
  const projected = today > 0 ? (acc / today) * daysTotal : 0;

  return { daysTotal, today: Math.min(today, daysTotal), budget, cumulative, projected };
}

// ---------------------------------------------------------------------------
// Месяц к месяцу (к одному и тому же дню)
// ---------------------------------------------------------------------------

export interface MoMData {
  day: number;
  cur: number;
  prev: number;
  curLabel: string;
  prevLabel: string;
}

/** Повседневные траты к N-му числу: текущий vs прошлый месяц (по сырым данным). */
export function buildMoM(data: BootstrapData, now: Date = new Date()): MoMData | null {
  const day = now.getDate();
  const catBy = new Map(data.categories.map((c) => [c.name, c] as const));
  const curKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const prevDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const prevKey = `${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, '0')}`;

  let cur = 0;
  let prev = 0;
  for (const op of data.operations) {
    const cat = catBy.get(op.category);
    const t = cat?.type ?? op.type;
    if (t !== 'Расход') continue;
    if (cat && !cat.is_daily) continue;
    const d = Number(op.date.slice(8, 10));
    if (d > day) continue;
    if (op.date.startsWith(curKey)) cur += op.amount;
    else if (op.date.startsWith(prevKey)) prev += op.amount;
  }
  if (prev === 0 && cur === 0) return null;
  return {
    day,
    cur,
    prev,
    curLabel: RU_MON_SHORT[now.getMonth()],
    prevLabel: RU_MON_SHORT[prevDate.getMonth()],
  };
}

// ---------------------------------------------------------------------------
// Стопки последних месяцев по группам
// ---------------------------------------------------------------------------

export interface StackedMonth {
  key: string;
  label: string;
  total: number;
  segments: { name: string; value: number; color: string }[];
}

// ---------------------------------------------------------------------------
// Норма сбережений и сумма отложенного по месяцам
// ---------------------------------------------------------------------------

export interface SavingsMonth {
  key: string;
  label: string;
  income: number;
  expense: number;
  saved: number;
  rate: number; // 0..1, может быть отрицательной
}

export function buildSavingsTrend(data: BootstrapData, months = 6, now: Date = new Date()): SavingsMonth[] {
  const catType = new Map(data.categories.map((c) => [c.name, c.type] as const));
  const out: SavingsMonth[] = [];
  for (let i = months - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    let income = 0;
    let expense = 0;
    for (const op of data.operations) {
      if (!op.date.startsWith(key)) continue;
      const t = catType.get(op.category) ?? op.type;
      if (t === 'Доход') income += op.amount;
      else if (t === 'Расход') expense += op.amount;
    }
    const saved = income - expense;
    out.push({ key, label: RU_MON_SHORT[d.getMonth()], income, expense, saved, rate: income > 0 ? saved / income : 0 });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Повседневные траты по дням недели (за N дней)
// ---------------------------------------------------------------------------

export interface WeekdaySpend {
  /** Пн..Вс. */
  labels: string[];
  avg: number[];
  max: number;
}

export function buildWeekdaySpend(data: BootstrapData, days = 90, now: Date = new Date()): WeekdaySpend {
  const catBy = new Map(data.categories.map((c) => [c.name, c] as const));
  const since = new Date(now.getFullYear(), now.getMonth(), now.getDate() - days);
  const sums = new Array(7).fill(0); // индекс 0=Пн
  const counts = new Array(7).fill(0); // число уникальных дат с тратами по weekday
  const seen = new Set<string>();

  for (const op of data.operations) {
    const cat = catBy.get(op.category);
    const t = cat?.type ?? op.type;
    if (t !== 'Расход') continue;
    if (cat && !cat.is_daily) continue;
    const m = op.date.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) continue;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    if (d < since) continue;
    const wd = (d.getDay() + 6) % 7;
    sums[wd] += op.amount;
    if (!seen.has(op.date)) {
      seen.add(op.date);
      counts[wd] += 1;
    }
  }
  const avg = sums.map((s, i) => (counts[i] > 0 ? s / counts[i] : 0));
  return { labels: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'], avg, max: Math.max(...avg, 1) };
}

// ---------------------------------------------------------------------------
// Топ трат текущего месяца
// ---------------------------------------------------------------------------

export interface TopSpend {
  id: string;
  category: string;
  amount: number;
  date: string;
  comment: string;
}

export function buildTopSpends(data: BootstrapData, n = 5, now: Date = new Date()): TopSpend[] {
  const key = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const catType = new Map(data.categories.map((c) => [c.name, c.type] as const));
  return data.operations
    .filter((op) => op.date.startsWith(key) && (catType.get(op.category) ?? op.type) === 'Расход')
    .sort((a, b) => b.amount - a.amount)
    .slice(0, n)
    .map((op) => ({ id: op.id, category: op.category, amount: op.amount, date: op.date, comment: op.comment }));
}

// ---------------------------------------------------------------------------
// НАКОПЛЕНИЯ
// ---------------------------------------------------------------------------

/** Рост капитала: нарастающий итог отложенного по месяцам. */
export interface CapitalPoint { key: string; label: string; cumulative: number; saved: number }

export function buildCapitalTrend(data: BootstrapData, months = 6, now: Date = new Date()): CapitalPoint[] {
  const trend = buildSavingsTrend(data, months, now);
  let acc = 0;
  return trend.map((m) => {
    acc += m.saved;
    return { key: m.key, label: m.label, cumulative: acc, saved: m.saved };
  });
}

/** Прогноз достижения цели по плановому взносу (или по доп. взносу из what-if). */
export interface GoalForecast {
  name: string;
  tier: 'foundation' | 'need' | 'dream';
  have: number;
  need: number;
  remaining: number;
  monthlyPlan: number;
  /** Месяцев до цели при заданном взносе (null — если взнос 0). */
  monthsAt(extraPerMonth: number): number | null;
}

export function buildGoalForecasts(data: BootstrapData): GoalForecast[] {
  return (data.goals ?? [])
    .filter((g) => g.need > 0 && g.remaining > 0)
    .map((g) => ({
      name: g.name,
      tier: g.tier,
      have: g.have,
      need: g.need,
      remaining: g.remaining,
      monthlyPlan: g.monthly_plan || 0,
      monthsAt(extra: number) {
        const rate = (g.monthly_plan || 0) + extra;
        if (rate <= 0) return null;
        return Math.ceil(g.remaining / rate);
      },
    }));
}

/** Подушка: месяцы защиты сейчас, цель, средний расход. */
export interface CushionState {
  have: number;
  need: number;
  avgExpense: number;
  haveMonths: number;
  needMonths: number;
  monthlyPlan: number;
}

export function buildCushion(data: BootstrapData, avgExpense: number): CushionState | null {
  const f = (data.goals ?? []).find((g) => g.tier === 'foundation');
  if (!f) return null;
  return {
    have: f.have,
    need: f.need,
    avgExpense,
    haveMonths: avgExpense > 0 ? f.have / avgExpense : 0,
    needMonths: avgExpense > 0 ? f.need / avgExpense : 0,
    monthlyPlan: f.monthly_plan || 0,
  };
}

/** Вклад категорий в экономию: топ повседневных, экономия 20% → за год. */
export interface ThriftItem { name: string; monthlyAvg: number; saveYear: number }

export function buildThrift(data: BootstrapData, months = 3, now: Date = new Date(), cut = 0.2): ThriftItem[] {
  const catBy = new Map(data.categories.map((c) => [c.name, c] as const));
  const since = new Date(now.getFullYear(), now.getMonth() - months, 1);
  const sums = new Map<string, number>();
  const monthsSeen = new Set<string>();
  for (const op of data.operations) {
    const cat = catBy.get(op.category);
    const t = cat?.type ?? op.type;
    if (t !== 'Расход') continue;
    if (cat && !cat.is_daily) continue;
    const m = op.date.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) continue;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    if (d < since) continue;
    sums.set(op.category, (sums.get(op.category) || 0) + op.amount);
    monthsSeen.add(op.date.slice(0, 7));
  }
  const n = Math.max(1, monthsSeen.size);
  return Array.from(sums.entries())
    .map(([name, total]) => ({ name, monthlyAvg: total / n, saveYear: (total / n) * cut * 12 }))
    .sort((a, b) => b.monthlyAvg - a.monthlyAvg)
    .slice(0, 4);
}

export function buildStacked(
  data: BootstrapData,
  months = 6,
  now: Date = new Date(),
): StackedMonth[] {
  const catBy = new Map(data.categories.map((c) => [c.name, c] as const));
  const colors = buildGroupColors(data);

  const out: StackedMonth[] = [];
  for (let i = months - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const byGroup = new Map<string, number>();
    for (const op of data.operations) {
      if (!op.date.startsWith(key)) continue;
      const cat = catBy.get(op.category);
      const t = cat?.type ?? op.type;
      if (t !== 'Расход') continue;
      const g = cat?.grp || NO_GROUP;
      byGroup.set(g, (byGroup.get(g) || 0) + op.amount);
    }
    const segments = Array.from(byGroup.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([name, value]) => ({ name, value, color: colors.get(name) ?? '#8A8276' }));
    out.push({
      key,
      label: RU_MON_SHORT[d.getMonth()],
      total: segments.reduce((s, x) => s + x.value, 0),
      segments,
    });
  }
  return out;
}
